/**
 * Comments and findings on places in a document or spreadsheet, in the item page's side panel (notesSlot.tsx; docs/
 * DOCUMENTS.md §12, "Comments and findings in place"): its threads, in the order of their places, the one picked (in
 * the text, or here) marked, each with a reply box and Resolve (Reopen, resolved); a new comment on the selection, or
 * a finding of your review: written one at a time on places (kept in this browser), then sent together as one review
 * with a summary, as GitHub's are. Findings sent are the panel's Issues (marked in the text too). Those made on an
 * earlier version of the file say so, quietly, and whether their text has changed since; one whose place is gone says
 * that.
 */

import type { ComponentChildren } from "preact";
import { useEffect, useState } from "preact/hooks";
import { api } from "../../api/client.ts";
import type { CommentOut, Severity, SpaceOut } from "../../api/types.gen.ts";
import { t } from "../../i18n/index.ts";
import { closeActions, type DiscussionCtx, sendReply, speakersOf } from "../../lib/conversation.ts";
import { spaceApi } from "../../lib/paths.ts";
import { asApiError } from "../../lib/resource.ts";
import { toast } from "../../lib/toast.ts";
import type { Note, NotesPane, PendingReview, Placed } from "../../office/review/notes.ts";
import { Button, InlineButton } from "../../ui/Button.tsx";
import { SeverityLabel } from "../../ui/counts.tsx";
import { Select, Textarea } from "../../ui/form.tsx";
import { Badge } from "../../ui/misc.tsx";
import { SeverityPicker } from "../../ui/SeverityPicker.tsx";
import { CommentView } from "../comments/comment.tsx";
import { Composer } from "../comments/composer.tsx";
import { Markdown } from "../comments/markdown.tsx";
import { NOTES_ID, reviewAsked } from "./notesSlot.ts";

/** A note's colours (its frame, its picked ring, its quote's line): a comment's purple, a finding's its severity's. */
const LOOKS: Record<string, { frame: string; picked: string; line: string }> = {
  comment: {
    frame: "border-violet-300",
    picked: "border-violet-400 ring-2 ring-violet-200",
    line: "border-violet-300",
  },
  error: { frame: "border-red-300", picked: "border-red-400 ring-2 ring-red-200", line: "border-red-300" },
  warning: { frame: "border-amber-300", picked: "border-amber-400 ring-2 ring-amber-200", line: "border-amber-300" },
  info: { frame: "border-blue-300", picked: "border-blue-400 ring-2 ring-blue-200", line: "border-blue-300" },
};
const lookOf = (kind: string | undefined) => LOOKS[kind ?? "comment"] ?? LOOKS["comment"];

type PaneProps = NotesPane & {
  space: SpaceOut;
  item: string;
  /** The rules a finding may say it breaks (the item's). */
  rules?: readonly { id: string; name: string }[];
};

/** What both sections draw from the pane: the discussion's context, the notes where they are, a note's card. */
function usePane(o: PaneProps) {
  const said = o.conv.data?.data ?? [];
  const ctx: DiscussionCtx = {
    space: o.space,
    topic: { item: o.item },
    reload: o.conv.reload,
    people: new Set(said.flatMap((c) => (c.author ? [c.author] : []))),
    speakers: speakersOf(said, []),
    seen: undefined,
  };
  // Threads and your review's findings to come; findings sent are the panel's Issues.
  const placed = o.notes.filter((n) => !n.issue).map((n) => ({ n, p: o.place(n) }));
  // In the order of their places; those whose place is gone after.
  placed.sort((a, b) => (a.p?.order ?? Number.POSITIVE_INFINITY) - (b.p?.order ?? Number.POSITIVE_INFINITY));
  const card = ({ n, p }: (typeof placed)[number]) => (
    <Card
      ctx={ctx}
      n={n}
      p={p}
      version={o.version}
      active={o.active === n.key}
      onActive={() => o.onActive(n.key)}
      onDone={() => o.onActive(null)}
      reloadIssues={o.reloadIssues}
      review={o.review}
      rules={o.rules ?? []}
    />
  );
  const done = (key: string | null) => {
    o.onPending(null);
    if (key) {
      o.onActive(key);
    }
  };
  return { ctx, placed, card, done };
}

/** The side panel's comments: threads on places, a new one on the selection. */
export function ReviewPane(o: PaneProps) {
  const [resolvedShown, setResolvedShown] = useState(false);
  const { ctx, placed, card, done } = usePane(o);
  const threads = placed.filter(({ n }) => n.thread);
  const resolved = threads.filter(({ n }) => n.resolved).length;
  const shown = threads.filter(({ n }) => resolvedShown || !n.resolved);
  const pending = o.pending && !o.pending.finding ? o.pending : null;
  return (
    <section id={NOTES_ID} class="mb-8 flex scroll-m-8 flex-col gap-2" aria-label={t("notes.pane")}>
      <div class="mb-1 flex items-center gap-2">
        <h2 class="flex-1 text-sm font-semibold tracking-wide text-zinc-500 uppercase">{t("notes.pane")}</h2>
        {resolved > 0 ? (
          <InlineButton size="xs" aria-pressed={resolvedShown} onClick={() => setResolvedShown(!resolvedShown)}>
            <span class="whitespace-nowrap">{t("notes.showResolved", { count: resolved })}</span>
          </InlineButton>
        ) : null}
        <Button size="xs" onMouseDown={(e: MouseEvent) => e.preventDefault()} onClick={o.onNew}>
          {t("notes.new")}
        </Button>
      </div>
      {pending ? <NewNote ctx={ctx} item={o.item} pending={pending} review={o.review} onDone={done} /> : null}
      {shown.length === 0 && !pending ? <p class="text-sm text-zinc-500">{t("notes.empty")}</p> : null}
      <ul class="flex flex-col gap-2">
        {shown.map((x) => (
          <li key={x.n.key}>{card(x)}</li>
        ))}
      </ul>
    </section>
  );
}

/**
 * At the top of the side panel's issues: a new finding on the selection, and your review not sent yet (its findings
 * in it, pinned there until it's sent or dropped).
 */
export function ReviewFindings(o: PaneProps) {
  const { ctx, placed, card, done } = usePane(o);
  const drafts = placed.filter(({ n }) => n.draft);
  const pending = o.pending?.finding && o.pending.at !== null ? o.pending : null;
  // A finding with nothing selected: about the item as a whole, in its review form (the page's).
  const placeless = o.pending?.finding === true && o.pending.at === null;
  const { onPending } = o;
  useEffect(() => {
    if (placeless) {
      onPending(null);
      reviewAsked.value += 1;
    }
  }, [placeless, onPending]);
  return (
    <div class="mb-4 flex flex-col gap-2">
      <div class="flex justify-end">
        <Button size="xs" onMouseDown={(e: MouseEvent) => e.preventDefault()} onClick={o.onFinding}>
          {t("notes.addFinding")}
        </Button>
      </div>
      {drafts.length > 0 ? (
        <YourReview review={o.review}>
          {drafts.map((d) => (
            <li key={d.n.key}>{card(d)}</li>
          ))}
        </YourReview>
      ) : null}
      {pending ? (
        <NewNote ctx={ctx} item={o.item} pending={pending} review={o.review} rules={o.rules} onDone={done} />
      ) : null}
    </div>
  );
}

/** A finding's fields as it's written or changed: its severity, the rule it breaks (if any), what's wrong. */
interface FindingFields {
  severity: Severity;
  rule: string;
  message: string;
}

/** A finding of your review being written, or changed: its fields, then `submit`'s button (or cancelling). */
function FindingForm({
  quote,
  initial = { severity: "warning", rule: "", message: "" },
  rules,
  submit,
  onSubmit,
  onCancel,
}: {
  quote: string;
  initial?: FindingFields;
  rules: readonly { id: string; name: string }[];
  /** Its button's words. */
  submit: string;
  onSubmit: (f: FindingFields) => void;
  onCancel: () => void;
}) {
  const [f, setF] = useState(initial);
  return (
    <div class={`flex flex-col gap-2 rounded-md border bg-white p-3 ${lookOf(f.severity)?.frame}`}>
      {quote ? <Quote text={quote} kind={f.severity} /> : null}
      <SeverityPicker
        label={t("review.severity")}
        value={f.severity}
        onValue={(severity) => setF({ ...f, severity })}
        class="self-start"
      />
      {rules.length > 0 ? (
        <Select
          aria-label={t("review.rule")}
          value={f.rule}
          options={[{ value: "", label: t("notes.noRule") }, ...rules.map((r) => ({ value: r.id, label: r.name }))]}
          onValue={(rule) => setF({ ...f, rule })}
          compact={true}
        />
      ) : null}
      <Textarea
        aria-label={t("review.message")}
        placeholder={t("notes.findingPlaceholder")}
        value={f.message}
        onValue={(message) => setF({ ...f, message })}
        rows={3}
        autoFocus={true}
      />
      <div class="flex gap-2">
        <Button
          size="sm"
          variant="primary"
          disabled={!f.message.trim()}
          onClick={() => onSubmit({ ...f, message: f.message.trim() })}
        >
          {submit}
        </Button>
        <Button size="sm" onClick={onCancel}>
          {t("common.cancel")}
        </Button>
      </div>
    </div>
  );
}

/** What's written on the place selected: a comment (a thread of its own), or a finding of your review (`finding`). */
function NewNote({
  ctx,
  item,
  pending,
  review,
  rules = [],
  onDone,
}: {
  ctx: DiscussionCtx;
  item: string;
  pending: { at: unknown; quote: string; finding: boolean };
  review: PendingReview;
  /** The rules a finding may say it breaks (the item's). */
  rules?: readonly { id: string; name: string }[] | undefined;
  onDone: (key: string | null) => void;
}) {
  if (pending.finding) {
    return (
      <FindingForm
        quote={pending.quote}
        rules={rules}
        submit={t("notes.addToReview")}
        onCancel={() => onDone(null)}
        onSubmit={(f) => {
          review.add({
            at: pending.at,
            quote: pending.quote,
            severity: f.severity,
            message: f.message,
            ...(f.rule ? { rule: f.rule } : {}),
          });
          onDone(`d:${review.list.length}`);
        }}
      />
    );
  }
  return (
    <div class={`flex flex-col gap-2 rounded-md border bg-white p-3 ${lookOf(undefined)?.frame}`}>
      {pending.quote ? <Quote text={pending.quote} /> : null}
      <Composer
        space={ctx.space}
        about={{ item, people: ctx.people, speakers: ctx.speakers }}
        placeholder={t("comments.placeholder")}
        sendLabel={t("comments.send")}
        autoFocus={true}
        onCancel={() => onDone(null)}
        send={async (body) => {
          // On its place; with none (nothing was selected), on the item as a whole.
          const c = await api.post<CommentOut>(spaceApi(ctx.space, "/comments"), {
            item,
            body,
            ...(pending.at ? { anchor: pending.at } : {}),
          });
          ctx.reload();
          onDone(pending.at ? `c:${c.id}` : null);
        }}
      />
    </div>
  );
}

/** Your review being written: how many findings, a summary, and sending it (or dropping it). */
function YourReview({ review, children }: { review: PendingReview; children: ComponentChildren }) {
  const [summary, setSummary] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <div class="flex flex-col gap-2 rounded-md border border-zinc-300 bg-zinc-50 p-3">
      <div class="flex flex-wrap items-center gap-x-2 gap-y-1">
        <p class="flex-1 text-sm font-medium text-zinc-900">{t("notes.yourReview", { count: review.list.length })}</p>
        <Badge tone="amber">{t("notes.notSent")}</Badge>
      </div>
      <ul class="flex flex-col gap-2">{children}</ul>
      <Textarea
        aria-label={t("notes.summary")}
        placeholder={t("notes.summary")}
        value={summary}
        onValue={setSummary}
        rows={2}
      />
      <div class="flex gap-2">
        <Button
          size="sm"
          variant="primary"
          busy={busy}
          onClick={() => {
            setBusy(true);
            review
              .submit(summary)
              .then(() => {
                setSummary("");
                toast(t("review.saved"));
              })
              .catch((e: unknown) => toast(asApiError(e).message, "error"))
              .finally(() => setBusy(false));
          }}
        >
          {t("notes.submit")}
        </Button>
        <Button size="sm" onClick={review.discard}>
          {t("notes.discard")}
        </Button>
      </div>
    </div>
  );
}

/** The text a note is on, a line or two of it. */
function Quote({ text, kind }: { text: string; kind?: string | undefined }) {
  return <p class={`line-clamp-2 border-l-2 pl-2 text-xs text-zinc-600 ${lookOf(kind)?.line}`}>{text}</p>;
}

function Card({
  ctx,
  n,
  p,
  version,
  active,
  onActive,
  onDone,
  reloadIssues,
  review,
  rules,
}: {
  /** The rules a finding may say it breaks (the item's). */
  rules: readonly { id: string; name: string }[];
  /** Resolved: no longer picked (a resolved thread isn't marked in the text). */
  onDone: () => void;
  review: PendingReview;
  ctx: DiscussionCtx;
  n: Note;
  p: Placed | null;
  version: string | null;
  active: boolean;
  onActive: () => void;
  reloadIssues: () => void;
}) {
  const [replying, setReplying] = useState(false);
  const [editing, setEditing] = useState(false);
  // A finding of your review not sent yet, changed as it was written.
  if (editing && n.draft) {
    const d = n.draft;
    return (
      <FindingForm
        quote={p?.quote ?? d.quote}
        initial={{ severity: d.severity, rule: d.rule ?? "", message: d.message }}
        rules={rules}
        submit={t("common.save")}
        onCancel={() => setEditing(false)}
        onSubmit={(f) => {
          const { rule: _, ...rest } = d;
          review.update(Number(n.key.slice(2)), {
            ...rest,
            severity: f.severity,
            message: f.message,
            ...(f.rule ? { rule: f.rule } : {}),
          });
          setEditing(false);
        }}
      />
    );
  }
  const earlier = n.on !== null && version !== null && n.on !== version;
  // A note's own marks: where it is, and whether that's as it was.
  const where = p === null ? t("notes.gone") : earlier ? (p.changed ? t("notes.changed") : t("notes.earlier")) : null;
  const state = async (s: "resolved" | "reopened") => {
    await api.post(spaceApi(ctx.space, "/comments"), { item: ctx.topic.item, reply_to: n.thread?.root.id, state: s });
    ctx.reload();
    if (s === "resolved") {
      onDone();
    }
  };
  const close = (how: 0 | 1) => {
    const i = n.issue;
    if (!(i?.id && ctx.topic.item)) {
      return;
    }
    const [resolve, dismiss] = closeActions({
      space: ctx.space,
      item: ctx.topic.item,
      finding: i.id,
      asOf: undefined,
      onDone: reloadIssues,
    });
    (how === 0 ? resolve : dismiss)?.run("").catch((e: unknown) => toast(String(e), "error"));
  };
  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents lint/a11y/noStaticElementInteractions: its buttons are the keyboard's; a click anywhere on it picks it.
    <div
      class={`flex flex-col gap-2 rounded-md border bg-white p-3 ${active ? lookOf(n.severity)?.picked : "border-zinc-200"} ${n.resolved ? "opacity-70" : ""}`}
      data-note={n.key}
      onClick={onActive}
    >
      {p?.quote ? <Quote text={p.quote} kind={n.severity} /> : null}
      {where ? <p class="text-xs text-zinc-500">{where}</p> : null}
      {n.draft ? (
        <>
          <div class="flex items-center gap-2 text-xs">
            <SeverityLabel severity={n.draft.severity} />
            {n.draft.rule ? (
              <span class="text-zinc-500">{rules.find((r) => r.id === n.draft?.rule)?.name ?? n.draft.rule}</span>
            ) : null}
          </div>
          <p class="text-sm whitespace-pre-wrap text-zinc-900" data-selectable="">
            {n.draft.message}
          </p>
          <div class="flex gap-1">
            <InlineButton size="xs" onClick={() => setEditing(true)}>
              {t("comments.edit")}
            </InlineButton>
            <InlineButton size="xs" tone="danger" onClick={() => review.remove(Number(n.key.slice(2)))}>
              {t("notes.remove")}
            </InlineButton>
          </div>
        </>
      ) : null}
      {n.issue ? (
        <>
          <div class="flex items-center gap-2 text-xs">
            <SeverityLabel severity={n.issue.severity} />
            <span class="truncate text-zinc-600">{n.issue.reviewer_name}</span>
          </div>
          <Markdown space={ctx.space} text={n.issue.message} class="text-sm" />
          <div class="flex gap-1">
            <InlineButton size="xs" onClick={() => close(0)}>
              {t("notes.resolve")}
            </InlineButton>
            <InlineButton size="xs" onClick={() => close(1)}>
              {t("notes.dismiss")}
            </InlineButton>
          </div>
        </>
      ) : null}
      {n.thread ? (
        <>
          <CommentView ctx={ctx} c={n.thread.root} place={false} onReply={() => setReplying(true)} />
          {n.thread.replies.length > 0 ? (
            <ul class="ml-2 flex flex-col gap-2 border-l border-zinc-200 pl-2">
              {n.thread.replies.map((r) => (
                <li key={r.id}>
                  <CommentView ctx={ctx} c={r} onReply={() => setReplying(true)} />
                </li>
              ))}
            </ul>
          ) : null}
          {replying ? (
            <Composer
              space={ctx.space}
              about={{ item: ctx.topic.item, people: ctx.people, speakers: ctx.speakers }}
              placeholder={t("comments.replyPlaceholder")}
              sendLabel={t("comments.reply")}
              autoFocus={true}
              compact={true}
              onCancel={() => setReplying(false)}
              send={async (body) => {
                const root = n.thread?.root;
                if (root) {
                  await sendReply(ctx, {}, root, body);
                }
                setReplying(false);
              }}
            />
          ) : (
            <div class="flex gap-1">
              <InlineButton size="xs" onClick={() => setReplying(true)}>
                {t("comments.reply")}
              </InlineButton>
              <InlineButton
                size="xs"
                onClick={() => state(n.resolved ? "reopened" : "resolved").catch(() => undefined)}
              >
                {n.resolved ? t("notes.reopen") : t("notes.resolve")}
              </InlineButton>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}
